// CompsSearch.ts

import { useEffect } from "react"
import {
  compSearchViewed,
  searchedCompSubmitted,
} from "@/analytics/events/analyzer"
import { router, useLocalSearchParams } from "expo-router"
import Search from "@/screens/Search"
import { useCreateAnalysisComp } from "@/queries/analysis"
import { showActionToast, showErrorToast } from "@/components/toasts/AppToast"
import {
  addComp,
  setCompUpdatingState,
  triggerScrollToRecent,
} from "@/stores/compsDataStore"
import { type AnalysisCompType, type AnalysisType } from "@/generated/graphql"
import { BFScreen } from "@/components/standard/screen/BFScreen"

export default function CompsSearch() {
  const searchParams = useLocalSearchParams()
  const { placeId, analysisId } = searchParams

  // Ensure analysisId is a single string value
  const resolvedAnalysisId = Array.isArray(analysisId)
    ? analysisId[0]
    : analysisId

  const { mutate: createComp } = useCreateAnalysisComp()

  useEffect(() => {
    compSearchViewed()
  }, [])

  const onSelectPlace = (
    placeId: string | null,
    street: string,
    fullAddress: string
  ) => {
    if (resolvedAnalysisId && placeId) {
      searchedCompSubmitted({ propertyAddress: fullAddress })

      // Start the dimming/loading state
      setCompUpdatingState(true)

      // Call the mutation with custom success and error handlers
      createComp(
        { analysisId: resolvedAnalysisId, address: fullAddress },
        {
          onSuccess: (data) => {
            const compData = data.createAnalysisComp

            if (compData?.success && compData.statusCode === 200) {
              updateCompStoreState(compData.analysis as AnalysisType)
              showActionToast({
                title: "Success!",
                message:
                  "We added the property to the list.\nClick here to scroll to it.",
                onPress: onScrollToRecent,
              })
            } else if (compData?.success && compData.statusCode === 201) {
              updateCompStoreState(compData.analysis as AnalysisType)
              showActionToast({
                title: "Property already in comp list",
                message:
                  "This property is already in the comp list.\nClick here to view it.",
                onPress: onScrollToRecent,
              })
            } else if (compData?.message && compData.statusCode === 404) {
              showErrorToast({
                title: "Comp Data Not Found",
                message:
                  "Sorry! We couldn’t find information on that property.", // compData.message,
              })
            }
          },
          onError: (error: any) => {
            console.log(`*comp create error- ${JSON.stringify(error)}`)
            const errorResponse = error?.response?.data
            if (errorResponse?.errors) {
              showErrorToast({
                title: "Error",
                message: errorResponse.errors[0].message,
              })
            } else {
              showErrorToast({
                title: "Error!",
                message: "An unknown error occurred. Please try again.",
              })
            }
          },
          onSettled: () => {
            // End the dimming/loading state regardless of success or error
            setCompUpdatingState(false)
          },
        }
      )

      router.push(`/properties/${resolvedAnalysisId}/`)
    }
  }

  const updateCompStoreState = (analysis?: AnalysisType) => {
    if (!analysis?.latestManualComp) {
      console.error("*No comp data found")
      return
    }

    const newComp: AnalysisCompType = analysis.latestManualComp
    if (newComp) {
      addComp(newComp)
    }
  }

  const onScrollToRecent = () => {
    console.log("TODO: Scrolling to recent")
    triggerScrollToRecent()
  }

  return (
    <BFScreen dark={true} gradientBackground={true} noBackButton={true}>
      <Search
        title="Comp Search"
        placeId={placeId as string}
        onSelectPlace={onSelectPlace}
      />
    </BFScreen>
  )
}
